/**
 * Pure helpers for syncing TipTap body HTML with a full-document `content` prop.
 * Kept separate from the hook so save/subscription race logic is testable.
 */

/**
 * TipTap's empty document is `<p></p>`, not `''`. An editor opened on a
 * superseded email (body emptied on the workspace tick) mounts that way while
 * the version fetch is still in flight; treating it as an unsaved edit would
 * block the fetched body from ever landing.
 */
export const isEmptyEditorBody = (html: string): boolean => {
  const trimmed = html.trim();
  return trimmed === '' || trimmed === '<p></p>';
};

/** Full HTML from the prop matches what the editor would persist. */
export const editorMatchesIncomingContent = (
  editorBodyHTML: string,
  incomingContent: string,
  getContentToSave: (bodyHTML: string) => string
): boolean => getContentToSave(editorBodyHTML) === incomingContent;

/** Editor has local edits that differ from the last successful save. */
export const editorHasUnsavedChanges = (
  editorBodyHTML: string,
  lastSavedFullContent: string,
  getContentToSave: (bodyHTML: string) => string
): boolean => {
  if (isEmptyEditorBody(editorBodyHTML) && isEmptyEditorBody(lastSavedFullContent)) {
    return false;
  }
  return getContentToSave(editorBodyHTML) !== lastSavedFullContent;
};

/**
 * Whether a `content` prop update should replace the TipTap document.
 * Avoids redundant setContent (cursor jump) and stale subscription echoes.
 */
export const shouldApplyExternalEditorContent = (params: {
  incomingContent: string;
  editorBodyHTML: string;
  lastSavedFullContent: string;
  awaitingSaveEcho: boolean;
  getContentToSave: (bodyHTML: string) => string;
}): boolean => {
  const {
    incomingContent,
    editorBodyHTML,
    lastSavedFullContent,
    awaitingSaveEcho,
    getContentToSave
  } = params;

  if (editorMatchesIncomingContent(editorBodyHTML, incomingContent, getContentToSave)) {
    return false;
  }

  if (editorHasUnsavedChanges(editorBodyHTML, lastSavedFullContent, getContentToSave)) {
    return false;
  }

  // After a save, ignore subscription payloads until we see our saved content echo
  // back (or the user edits again). Prevents briefly reverting to stale workspace data.
  if (awaitingSaveEcho && incomingContent !== lastSavedFullContent) {
    return false;
  }

  return true;
};

/** Clear the post-save echo guard once the prop reflects the saved document. */
export const shouldClearAwaitingSaveEcho = (
  incomingContent: string,
  lastSavedFullContent: string
): boolean => incomingContent === lastSavedFullContent;
